import { TypedDocumentNode, gql, useQuery } from '@apollo/client';
import { LabOrganization, PartnerOrganization } from 'khshared/API';
import buildRequisitionFromServiceRequestExternalIDs from 'khshared/buildRequisitionFromServiceRequestExternalIDs';
import getErrorLoggingFields from 'khshared/getErrorLoggingFields';
import {
  BookingRedirectScreenCoordinatorQuery,
  BookingRedirectScreenCoordinatorQueryVariables,
} from 'khshared/graphql/types';
import isNotNullish from 'khshared/isNotNullish';
import getPartnerConfig from 'khshared/metadata/getPartnerConfig';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import KHAuth from '../../utils/KHAuth';
import { useWizardState } from '../../utils/useWizardState';
import { BOOKING_WIZARD } from './index';

const COORDINATOR_QUERY: TypedDocumentNode<
  BookingRedirectScreenCoordinatorQuery,
  BookingRedirectScreenCoordinatorQueryVariables
> = gql`
  query BookingRedirectScreenCoordinatorQuery($coordinatorID: ID!) {
    getCoordinator(id: $coordinatorID) {
      id
      outstandingPartnerOrganizationBookingURLs
    }
  }
`;

// A single place to redirect in order to bootstrap the wizard state machine
export default function BookingRedirectScreen(): null {
  const wizardState = useWizardState(BOOKING_WIZARD, 'booking-redirect-state');

  const location = useLocation();
  const routeParams = useParams<{ partnerOrganization?: Lowercase<PartnerOrganization> }>();

  const [specificServicesRequested, setSpecificServicesRequested] = useState<
    Partial<Record<LabOrganization, string[]>> | undefined
  >(undefined);

  const partnerOrganizationForAppointment = useRef<PartnerOrganization | null>(null);
  const partnerOrganizationBookingURLForAppointment = useRef<string | null>(null);

  const { data: coordinatorQuery, loading: coordinatorLoading, error: coordinatorError } = useQuery(
    COORDINATOR_QUERY,
    {
      skip: !KHAuth.isSignedIn(),
      variables: {
        coordinatorID: KHAuth.getViewerIDOrNull() ?? '',
      },
    },
  );

  // Log any errors that come up.
  useEffect(() => {
    if (coordinatorError != null)
      wizardState.logger.error({
        eventName: 'bookingRedirectQueryError',
        ...getErrorLoggingFields(coordinatorError),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinatorError]);

  // On page load parse tests and partner information from url.
  // If no partner is provided it just redirects the user
  // If a partner is provided is fires off a query to check login status and download test metadata
  useEffect(() => {
    const externalIDsByLabOrganization = (location.search.length !== 0
      ? new URLSearchParams(location.search).get('sr')?.split(',') ?? []
      : []
    ).reduce((acc, serviceRequest) => {
      const [lab, externalID] = serviceRequest.split(':');
      if (lab == null || !(lab in LabOrganization) || externalID == null) return acc;

      const labOrganization = lab as LabOrganization;
      if (acc[labOrganization] == null) acc[labOrganization] = [];
      acc[labOrganization].push(externalID);

      return acc;
    }, {} as Record<LabOrganization, string[]>);

    if (Object.entries(externalIDsByLabOrganization).length > 0)
      setSpecificServicesRequested(externalIDsByLabOrganization);

    // TODO(jones) we don't need to strip underscores once dr_villivalam is fully updated to drvillivalam in all cases
    const maybePartnerOrganization = routeParams.partnerOrganization
      ?.toUpperCase()
      .replace(/_/g, '');
    if (
      maybePartnerOrganization != null &&
      Object.values(PartnerOrganization).includes(maybePartnerOrganization as never)
    ) {
      const partnerOrganization = maybePartnerOrganization as PartnerOrganization;
      const partnerConfig = getPartnerConfig(partnerOrganization);

      if (!partnerConfig.isSelfBookingAllowed) {
        wizardState.next({ wizardState, name: 'show-booking-redirect-failure-action' }, 'replace');
        return;
      }

      partnerOrganizationForAppointment.current = partnerOrganization;
      partnerOrganizationBookingURLForAppointment.current = `${location.pathname}${location.search}`;

      if (Object.entries(externalIDsByLabOrganization).length === 0) {
        const { labOrganizations } = partnerConfig;
        if (
          Object.keys(labOrganizations).length === 1 &&
          partnerConfig.selectSingleServiceRequestExternalID !== undefined
        ) {
          const specificTest = {
            [partnerConfig.defaultLabOrganization]: [
              partnerConfig.selectSingleServiceRequestExternalID,
            ],
          };
          setSpecificServicesRequested(specificTest);
        } else {
          setSpecificServicesRequested(externalIDsByLabOrganization);
        }
      } else if (
        partnerConfig.requisitionUploadFromPatient !== 'required' &&
        partnerConfig.requisitionUploadFromScheduler !== 'required'
      ) {
        // If the URL doesn't have enough information to know what services a user wants and we
        // won't later ask the user to upload one or upload one ourselves, then this booking is
        // doomed.
        wizardState.next({ wizardState, name: 'show-booking-redirect-failure-action' }, 'replace');
      }
    } else {
      // If not partner is associated with the redirect just pass them through
      wizardState.next({ wizardState, name: 'redirect-action' }, 'replace');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the query completes or is determined not to be necessary set up the context and redirect the user
  useEffect(() => {
    if (coordinatorLoading || specificServicesRequested === undefined) return;

    if (partnerOrganizationForAppointment !== null) {
      wizardState.context.appointment.partnerOrganization =
        partnerOrganizationForAppointment.current;
      wizardState.context.appointment.partnerOrganizationBookingURL =
        partnerOrganizationBookingURLForAppointment.current;
    }

    if (specificServicesRequested !== undefined) {
      const requisitions = Object.entries(specificServicesRequested)
        .map(([lab, externalIDs]) =>
          buildRequisitionFromServiceRequestExternalIDs(lab as LabOrganization, externalIDs),
        )
        .filter(isNotNullish);

      wizardState.context.outstandingRequisitions = requisitions;
    }

    if (coordinatorQuery != null) {
      if (
        wizardState.context.appointment.partnerOrganization == null &&
        coordinatorQuery?.getCoordinator?.outstandingPartnerOrganizationBookingURLs != null &&
        coordinatorQuery.getCoordinator.outstandingPartnerOrganizationBookingURLs.length > 0
      ) {
        wizardState.next({ wizardState, name: 'show-booking-selection-action' }, 'replace');
        return;
      }
    }

    wizardState.next({ wizardState, name: 'redirect-action' }, 'replace');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinatorLoading, specificServicesRequested]);

  return null;
}
